import React from 'react';
import './MarketplaceHeartButton.css';
import { useLanguage } from '../../contexts/LanguageContext';

/**
 * Shared MarketplaceHeartButton
 *
 * Issue #385: When a marketplace object is sold, the heart (like) button
 * must be deactivated so that no further hearts can be given. Centralising
 * the logic in a shared component ensures the same behaviour and the same
 * accessibility treatment in every place a marketplace heart is rendered
 * (marketplace feed, "Mein Marktplatz", marketplace detail page).
 *
 * Variants:
 *  - "compact": small icon + count, used inside marketplace card footers.
 *  - "large":   icon-only square button used on the detail action bar.
 *
 * Accessibility:
 *  - Uses aria-pressed to convey toggle state.
 *  - Uses aria-disabled (instead of the native `disabled` attribute) so the
 *    button stays in the focus order and screen readers can still announce
 *    the reason it cannot be activated.
 *  - aria-label switches to a sold-specific message when disabled.
 *  - SVGs are aria-hidden because the button label fully describes them.
 */
const MarketplaceHeartButton = ({
  variant = 'compact',
  heartsCount = 0,
  userHasHearted = false,
  isSold = false,
  onToggle,
  className,
}) => {
  const { t } = useLanguage();

  const enabledLabel = userHasHearted
    ? (t('marketplace.removeFromFavorites') || t('marketplace.removeHeart') || 'Aus Favoriten entfernen')
    : (t('marketplace.addToFavorites') || t('marketplace.addHeart') || 'Zu Favoriten hinzufügen');
  const disabledLabel = t('marketplace.heartDisabledSold') || 'Like nicht möglich – Objekt ist verkauft';
  const ariaLabel = isSold ? disabledLabel : enabledLabel;

  const handleClick = (event) => {
    event.stopPropagation();
    if (isSold) {
      return;
    }
    if (onToggle) {
      onToggle(event);
    }
  };

  const baseClass = variant === 'large' ? 'shared-heart-button shared-heart-button--large' : 'shared-heart-button shared-heart-button--compact';
  const classes = [
    baseClass,
    userHasHearted ? 'is-hearted' : '',
    isSold ? 'is-disabled' : '',
    className || '',
  ].filter(Boolean).join(' ');

  return (
    <button
      type="button"
      className={classes}
      onClick={handleClick}
      aria-label={ariaLabel}
      aria-pressed={userHasHearted}
      aria-disabled={isSold || undefined}
      title={isSold ? disabledLabel : undefined}
    >
      <svg
        width={variant === 'large' ? '24' : '16'}
        height={variant === 'large' ? '24' : '16'}
        viewBox="0 0 24 24"
        fill={userHasHearted ? '#FF0000' : 'none'}
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
        focusable="false"
      >
        <path
          d="M20.84 4.61C20.3292 4.099 19.7228 3.69365 19.0554 3.41708C18.3879 3.14052 17.6725 2.99817 16.95 2.99817C16.2275 2.99817 15.5121 3.14052 14.8446 3.41708C14.1772 3.69365 13.5708 4.099 13.06 4.61L12 5.67L10.94 4.61C9.9083 3.57831 8.50903 2.99871 7.05 2.99871C5.59096 2.99871 4.19169 3.57831 3.16 4.61C2.1283 5.64169 1.54871 7.04096 1.54871 8.5C1.54871 9.95903 2.1283 11.3583 3.16 12.39L4.22 13.45L12 21.23L19.78 13.45L20.84 12.39C21.351 11.8792 21.7563 11.2728 22.0329 10.6054C22.3095 9.93789 22.4518 9.22249 22.4518 8.5C22.4518 7.77751 22.3095 7.0621 22.0329 6.39464C21.7563 5.72718 21.351 5.12075 20.84 4.61Z"
          stroke="#FF0000"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      {variant === 'compact' && (
        <span className="shared-heart-button__count">
          {heartsCount || 0}
        </span>
      )}
    </button>
  );
};

export default MarketplaceHeartButton;
